import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useStoreActions, useStoreState } from '@/stores/store';
import { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Switch } from '@/components/Switch';
import { Text } from '@/components/Text';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { togglePatternLockOption } from '../api/services/user';
import colors, { appTheme } from '@/config/colors';
import { StackScreenProps } from '@react-navigation/stack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';

export default function Lockscreen(
  props: StackScreenProps<TherapistStackParamList, 'Sperrbildschirm'>,
) {
  // logic
  const [showModalToggle, setShowModalToggle] = useState(false);
  const [showModalPatternLock, setShowModalPatternLock] = useState(false);
  const [showPatternLock, setShowPatternLock] = useState(false);
  const [showErrorToggleMessage, setShowErrorToggleMessage] = useState(false);
  const { appStartResumeLock, eigentrainingLock, appLockPattern } =
    useStoreState((state) => ({
      appStartResumeLock: state.appStartResumeLock,
      eigentrainingLock: state.eigentrainingLock,
      appLockPattern: state.appLockPattern,
    }));
  const { changeOnAppStartResumeLock, changeOnEigentrainingLock } =
    useStoreActions((actions) => ({
      changeOnAppStartResumeLock: actions.changeOnAppStartResumeLock,
      changeOnEigentrainingLock: actions.changeOnEigentrainingLock,
    }));
  const { showNotification } = useNotification();

  const toggleShowPatternLock = useCallback((state: boolean) => {
    setShowPatternLock(state);
  }, []);
  const toggleOnAppStartResumeLock = useCallback(async () => {
    if (appLockPattern === '') {
      setShowErrorToggleMessage(true);
      return;
    }
    const res = await togglePatternLockOption(
      'appStartResumeLock',
      !appStartResumeLock,
    );
    if (res === 200) {
      changeOnAppStartResumeLock();
    } else {
      setShowModalToggle(true);
    }
  }, [appStartResumeLock, appLockPattern]);
  const toggleOnEigentraining = useCallback(async () => {
    if (appLockPattern === '') {
      setShowErrorToggleMessage(true);
      return;
    }
    const res = await togglePatternLockOption(
      'eigentrainingLock',
      !eigentrainingLock,
    );
    if (res === 200) {
      changeOnEigentrainingLock();
    } else {
      setShowModalToggle(true);
    }
  }, [eigentrainingLock, appLockPattern]);

  useEffect(() => {
    const { setPatterLockFailed } = props.route.params ?? {};
    if (setPatterLockFailed === false) {
      showNotification({
        type: 'success',
        title: 'Sperrmuster gespeichert.',
      });
    }
    setShowModalPatternLock(setPatterLockFailed);
  }, [props.route.params]);

  useEffect(() => {
    if (showPatternLock) {
      return props.navigation.navigate('SetPassword');
    }

    return () => {
      setShowPatternLock(false);
      setShowErrorToggleMessage(false);
    };
  }, [showPatternLock]);

  return (
    <ScrollView contentContainerStyle={styles.containerStyle}>
      <View style={styles.body}>
        {/* Change Lock */}
        <View style={styles.lockMargin}>
          <Text
            style={[
              styles.bold_text,
              styles.errorText,
              showErrorToggleMessage || appLockPattern === ''
                ? styles.opacity1
                : styles.opacity0,
              Platform.OS !== 'web' && styles.paddingTop,
            ]}
          >
            Zum Sperren des Bildschirms muss ein Sperrmuster festgelegt sein.
          </Text>
          <Text style={styles.bold_text}>
            Das Sperrmuster wird auf allen mit diesem Benutzerkonto verbundenen
            Geräten gesetzt.
          </Text>
          <PressableNeolexon
            onPress={() => toggleShowPatternLock(true)}
            style={styles.change_container}
          >
            <View style={styles.change}>
              <Text style={styles.change_text}>Sperrmuster ändern</Text>
            </View>
          </PressableNeolexon>
        </View>

        {/* Toggle */}
        <View>
          <Text style={styles.question_text}>
            Wann soll die App durch das Sperrmuster geschützt werden?
          </Text>
          <View style={styles.box}>
            <View style={[styles.options_container, styles.borderStyle]}>
              <Text style={styles.options_text}>Bei jedem Öffnen der App</Text>
              <Switch
                onValueChange={toggleOnAppStartResumeLock}
                value={appLockPattern === '' ? false : appStartResumeLock}
              />
            </View>
            <View style={styles.options_container}>
              <Text style={styles.options_text}>
                Nach dem Eigentraining in der App (Klinik-Version)
              </Text>
              <Switch
                onValueChange={toggleOnEigentraining}
                value={appLockPattern === '' ? false : eigentrainingLock}
              />
            </View>
          </View>
        </View>
      </View>

      {/* Modal on Error */}
      <Modal
        supportedOrientations={[
          'landscape',
          'landscape-left',
          'landscape-right',
        ]}
        animationType="none"
        transparent={true}
        visible={showModalToggle}
        onRequestClose={() => {
          setShowModalToggle(false);
        }}
      >
        <View style={styles.centeredView}>
          <View style={styles.modalView}>
            <Text style={styles.modalTitle}>
              Keine Verbindung zum neolexon Server
            </Text>
            <View style={styles.underline} />
            <Text style={styles.modalText}>
              Zum Ändern der Einstellung wird eine Internetverbindung benötigt.
              Bitte überprüfen Sie Ihre Internetverbindung und versuchen es
              erneut.
            </Text>
            <Pressable
              style={styles.button}
              onPress={() => setShowModalToggle(false)}
            >
              <Text style={styles.modalButton}>OK</Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      {showModalPatternLock === true && (
        <Modal
          supportedOrientations={[
            'landscape',
            'landscape-left',
            'landscape-right',
          ]}
          animationType="none"
          transparent={true}
          visible={showModalPatternLock}
          onRequestClose={() => {
            setShowModalPatternLock(false);
          }}
        >
          <View style={styles.centeredView}>
            <View style={styles.modalView}>
              <Text style={styles.modalTitle}>
                Fehler beim Speichern des Sperrmusters.
              </Text>
              <View style={styles.underline} />
              <Text style={styles.modalText}>
                Zum Speichern des Sperrmusters wird eine Internetverbindung
                benötigt. Bitte überprüfen Sie Ihre Internetverbindung und
                versuchen Sie es erneut.
              </Text>
              <Pressable
                style={styles.button}
                onPress={() => setShowModalPatternLock(false)}
              >
                <Text style={styles.modalButton}>Schließen</Text>
              </Pressable>
            </View>
          </View>
        </Modal>
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  containerStyle: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingBottom: 25,
  },
  body: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bold_text: {
    fontWeight: '700',
  },
  question_text: {
    fontSize: 20,
    fontWeight: '500',
    marginBottom: 15,
  },
  change_container: {
    alignItems: 'center',
    marginTop: 15,
    backgroundColor: appTheme.primary,
    borderRadius: 4,
  },
  change: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
  },
  change_text: {
    fontSize: 18,
    color: colors.white,
    marginHorizontal: 10,
    fontWeight: '700',
  },
  box: {
    display: 'flex',
    flexDirection: 'column',
    borderColor: colors.black,
    borderWidth: 1,
  },
  options_container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
  },
  options_text: {
    fontWeight: '700',
    fontSize: 20,
  },
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#00000099',
  },
  underline: {
    width: '100%',
    height: 1,
    marginBottom: 10,
    backgroundColor: colors.grey,
  },
  modalView: {
    backgroundColor: 'white',
    padding: 10,
    alignItems: 'center',
    maxWidth: 500,
    marginHorizontal: 10,
  },
  button: {
    width: '100%',
    padding: 10,
    backgroundColor: colors.darkPurpleBlue,
    borderColor: 'transparent',
  },

  modalButton: {
    color: colors.white,
    fontWeight: '700',
    textAlign: 'center',
  },
  modalTitle: {
    marginBottom: 10,
    textAlign: 'center',
    opacity: 0.5,
    fontWeight: '700',
  },
  modalText: {
    marginBottom: 15,
    fontSize: 22,
  },
  lockMargin: { marginBottom: 140 },
  errorText: { color: colors.red, textAlign: 'center', marginBottom: 10 },
  borderStyle: { borderBottomColor: colors.black, borderBottomWidth: 1 },
  opacity0: {
    opacity: 0,
  },
  opacity1: {
    opacity: 1,
  },
  paddingTop: {
    paddingTop: 10,
  },
});
